(function () {
  'use strict';
  if (window.HealthyBaskets) return;
  const root = () => (window.Shopify?.routes?.root || '/');
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const path = value => typeof value === 'string' && /^\/(?!\/)/.test(value) ? value : root() + 'pages/custom-gift-basket';
  async function api(endpoint, body) {
    const controller = new AbortController();
    const timer = setTimeout(()=>controller.abort(),20000);
    let response;
    try { response = await fetch(root() + endpoint, {method:body ? 'POST':'GET', credentials:'same-origin', cache:'no-store', signal:controller.signal,
      headers:{'Accept':'application/json', ...(body ? {'Content-Type':'application/json'}:{})}, ...(body ? {body:JSON.stringify(body)}:{})});
    } finally { clearTimeout(timer); }
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to update your basket. Please try again.');
    return data;
  }
  const groups = cart => {
    const result = new Map();
    for (const item of cart.items) {
      const id = item.properties?._hi_basket_id;
      if (!id) continue;
      if (!result.has(id)) result.set(id, {id, items:[]});
      result.get(id).items.push(item);
    }
    for (const group of result.values()) {
      group.wrapper = group.items.find(x => x.properties._hi_basket_role === 'packaging');
      group.products = group.items.filter(x => x.properties._hi_basket_role === 'product');
      group.count = group.products.reduce((n,x) => n+x.quantity,0);
    }
    return result;
  };
  const manifest = items => {
    const sums = new Map();
    for (const item of items) {
      const id = Number(item.variant_id || item.id);
      sums.set(id, (sums.get(id) || 0) + item.quantity);
    }
    return JSON.stringify([...sums].sort((a,b) => a[0]-b[0]));
  };
  const addressFields = {name:'Ship to — Name',line1:'Ship to — Address',line2:'Ship to — Apartment',city:'Ship to — City',state:'Ship to — State',zip:'Ship to — ZIP'};
  const states = 'AL:Alabama|AK:Alaska|AZ:Arizona|AR:Arkansas|CA:California|CO:Colorado|CT:Connecticut|DE:Delaware|DC:District of Columbia|FL:Florida|GA:Georgia|HI:Hawaii|ID:Idaho|IL:Illinois|IN:Indiana|IA:Iowa|KS:Kansas|KY:Kentucky|LA:Louisiana|ME:Maine|MD:Maryland|MA:Massachusetts|MI:Michigan|MN:Minnesota|MS:Mississippi|MO:Missouri|MT:Montana|NE:Nebraska|NV:Nevada|NH:New Hampshire|NJ:New Jersey|NM:New Mexico|NY:New York|NC:North Carolina|ND:North Dakota|OH:Ohio|OK:Oklahoma|OR:Oregon|PA:Pennsylvania|RI:Rhode Island|SC:South Carolina|SD:South Dakota|TN:Tennessee|TX:Texas|UT:Utah|VT:Vermont|VA:Virginia|WA:Washington|WV:West Virginia|WI:Wisconsin|WY:Wyoming'.split('|').map(x=>x.split(':'));
  const separate = props => props?.['Basket delivery'] === 'Separate address';
  function validAddress(props) {
    return !separate(props) || (['name','line1','city'].every(key=>String(props[addressFields[key]] || '').trim().length>0) &&
      states.some(([code])=>code===props[addressFields.state]) && /^\d{5}(-\d{4})?$/.test(props[addressFields.zip] || '') && props['Ship to — Country']==='United States');
  }
  const shippingQuantity = cart => {
    return Math.max(0,groups(cart).size-1);
  };
  const feeLines = cart => cart.items.filter(x=>x.properties?._hi_basket_shipping_fee==='true');
  async function shippingProduct(cart) {
    if(cart.currency!=='USD')throw new Error('Additional basket shipping is available in USD only.');
    let product;
    try {product=await api('products/gift-basket-extra-shipping.js');}
    catch (_) {throw new Error('Additional basket shipping is currently unavailable. Please contact us before ordering multiple baskets.');}
    const variant=product.variants?.[0];
    if(product.variants?.length!==1 || !variant.available || variant.requires_shipping!==false || variant.price!==999)
      throw new Error('Additional basket shipping is currently unavailable. Please contact us before ordering multiple baskets.');
    return variant;
  }
  async function syncShipping(cart) {
    const quantity=shippingQuantity(cart), lines=feeLines(cart);
    // A single basket has no additional delivery supplement.
    const variant=quantity ? await shippingProduct(cart) : null;
    const matching=lines.length===1 && lines[0].variant_id===variant?.id && lines[0].quantity===quantity;
    let changed=false;
    if(!matching && (lines.length || quantity)){
      // Delete by line key: identical variants bought separately must remain untouched.
      if(lines.length)await api('cart/update.js',{updates:Object.fromEntries(lines.map(x=>[x.key,0]))});
      if(quantity)await api('cart/add.js',{items:[{id:variant.id,quantity,properties:{_hi_basket_shipping_fee:'true','Service':'Additional gift basket delivery','Rate':'$9.99 per basket after the first; checkout shipping is additional'}}]});
      changed=true;
    }
    const latest=changed ? await api('cart.js') : cart;
    const latestLines=feeLines(latest), expected=shippingQuantity(latest);
    if(expected!==quantity || (expected ? latestLines.length!==1 || latestLines[0].variant_id!==variant.id || latestLines[0].quantity!==expected : latestLines.length>0))
      throw new Error('Your basket shipping changed while saving. Please review your cart and retry.');
    if(expected && (latestLines[0].price!==999 || latestLines[0].final_line_price!==999*expected))
      throw new Error('The $9.99 delivery supplement cannot be discounted. Please remove the discount or contact us.');
    return {cart:latest,changed};
  }
  function addressText(props) {
    return separate(props) ? [props[addressFields.name],props[addressFields.line1],props[addressFields.line2],
      [props[addressFields.city],props[addressFields.state],props[addressFields.zip]].filter(Boolean).join(', '),'United States'].filter(Boolean).join('\n') : 'Use the shipping address entered at checkout.';
  }
  function valid(group) {
    return group?.wrapper?.quantity === 1 && group.items.filter(x=>x.properties._hi_basket_role==='packaging').length === 1 &&
      group.items.length === group.products.length+1 && group.count >= 1 && group.count <= 10 &&
      group.products.every(x=>Number.isInteger(x.quantity) && x.quantity>0) &&
      String(group.wrapper.properties['Gift Recipient Name'] || '').trim().length > 0 &&
      validAddress(group.wrapper.properties) &&
      group.wrapper.properties._hi_basket_pending !== 'true' &&
      group.wrapper.properties._hi_basket_manifest === manifest(group.products);
  }
  function card(group, currency) {
    const props = (group.wrapper || group.items[0]).properties;
    const url = path(props._hi_basket_builder);
    const money = n => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency',currency:currency || 'USD'}).format(n/100);
    return `<li class="gb-card" data-gb-cart-id="${escape(group.id)}"><h3>${escape(props['Gift Basket'] || 'Custom gift basket')}</h3><p>${group.count} of 10 pieces · Packaging included</p>${!valid(group)?'<p class="gb-error">This basket needs attention. Edit and save it before checkout.</p>':''}<ul>${group.products.map(item=>`<li>${item.image?`<img src="${escape(item.image)}" alt="" loading="lazy">`:''}<span>${escape(item.quantity)} × ${escape(item.product_title || item.title)}${item.variant_title && item.variant_title!=='Default Title'?' · '+escape(item.variant_title):''}</span><strong>${money(item.final_line_price ?? item.line_price)}</strong></li>`).join('')}</ul><p>For: ${escape(props['Gift Recipient Name'])}</p><p><strong>Delivery:</strong> ${escape(addressText(props))}</p>${props['Gift Message']?`<p>${escape(props['Gift Message'])}</p>`:''}${props['Special Instructions']?`<p>Instructions: ${escape(props['Special Instructions'])}</p>`:''}<strong>${money(group.items.reduce((sum,x)=>sum+(x.final_line_price ?? x.line_price),0))}</strong><div class="gb-card-actions"><a href="${escape(url)}?basket=${encodeURIComponent(group.id)}">Edit basket</a><button type="button" data-gb-remove="${escape(group.id)}">Remove basket</button><a href="${escape(url)}">Create another basket</a></div><p role="alert"></p></li>`;
  }
  let busy = false;
  async function remove(id) {
    const cart = await api('cart.js');
    const group = groups(cart).get(id);
    if (group) await api('cart/update.js', {updates:Object.fromEntries(group.items.map(x=>[x.key,0]))});
    await syncShipping(await api('cart.js'));
  }
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-gb-remove]');
    if (!button) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy) return;
    busy = true; button.disabled = true;
    try { await remove(button.dataset.gbRemove); location.reload(); }
    catch (error) { button.closest('.gb-card').querySelector('[role=alert]').textContent = error.message; }
    finally { busy = false; button.disabled = false; }
  }, true);
  // Check the latest cart before the theme's standard checkout paths, including drawer links.
  let allowingCheckout = false;
  async function checkCheckout(event, target, continueCheckout) {
    if (allowingCheckout || !document.querySelector('[data-gb-cart-id], [data-gb-shipping-fee], a[href*="/products/gift-basket-extra-shipping"]')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy) return;
    busy = true;
    try {
      const cart = await api('cart.js');
      if ([...groups(cart).values()].some(group=>!valid(group))) throw new Error('Please edit and save the incomplete gift basket before checkout.');
      const shipping=await syncShipping(cart);
      if(shipping.changed){location.assign(root()+'cart');return;} // Review the updated charge before paying.
      allowingCheckout = true;
      continueCheckout();
      setTimeout(()=>{allowingCheckout=false;},0);
    } catch(error) {
      let notice = document.querySelector('[data-gb-checkout-error]');
      if (!notice) { notice=document.createElement('p'); notice.dataset.gbCheckoutError=''; notice.className='gb-error'; notice.setAttribute('role','alert'); target.after(notice); }
      notice.textContent=error.message;
    } finally {busy=false;}
  }
  document.addEventListener('click', event=>{
    const target=event.target.closest('.btn-checkout, a[href$="/checkout"], [name="checkout"]');
    if (target) checkCheckout(event,target,()=>target.click());
  },true);
  document.addEventListener('submit', event=>{
    if (event.target.matches('form.cart-form')) checkCheckout(event,event.target,()=>event.target.requestSubmit(event.submitter || undefined));
  },true);
  window.HealthyBaskets={api,groups,manifest,valid,card,escape,path,remove,addressFields,states,separate,validAddress,addressText,shippingQuantity,shippingProduct,syncShipping};
})();
